import React, { useEffect, useState } from "react";
import { StepProps } from "../../types";
import { wait } from "utils/utils";
import { HealthHistoryEnum } from "components/Signup/store/state/types";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { FlexRow } from "ui-kit/Layout/Flex";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import { useSignupDispatch } from "components/Signup/store/hooks";
import {
  switchAnimal,
  updateAnimalById,
} from "components/Signup/store/slices/animals";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { Trans } from "translations";
import { useSequentialAnimals } from "components/Signup/flow/SignupFlow/useSequentialAnimals";

const HealthQuestions = ({
  previous,
  next,
  isValid,
  transitioning,
}: StepProps) => {
  const [note, setNote] = useState<string>("");

  const dispatch = useSignupDispatch();
  const { healthHistory, name, id } = useCurrentAnimal();

  const { nextAnimalId } = useSequentialAnimals();

  const scrollDown = () => {
    wait(0.5).then(() => {
      window.scrollTo({
        top: window.document.body.scrollHeight,
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    if (healthHistory?.includes(HealthHistoryEnum.TREATED_LAST_24_MONTHS)) {
      setNote(
        `Bitte beachte: Wir übernehmen keine Kosten für Behandlungsmaßnahmen oder Operationen aufgrund von vorangegangenen Beschwerden, die bereits vor Versicherungsbeginn bestanden/begonnen haben. Sofern ${name} 24 Monate keine Symptome zeigt und keine Behandlung oder Untersuchung zu dieser Vorerkrankung durchgeführt wurde, werden Kosten bei medizinischer Notwendigkeit wieder im Rahmen der Allgemeinen Versicherungsbedingungen erstattet.`
      );
      scrollDown();
    } else if (
      healthHistory?.includes(HealthHistoryEnum.ILLNESS_LAST_24_MONTHS)
    ) {
      setNote(
        `Bitte beachte: Wir übernehmen keine Kosten für Behandlungsmaßnahmen oder Operationen aufgrund von vorangegangenen Beschwerden, die bereits vor Versicherungsbeginn bestanden/begonnen haben. Sofern ${name} 24 Monate keine Symptome zeigt und keine Behandlung oder Untersuchung zu dieser Vorerkrankung durchgeführt wurde, werden Kosten bei medizinischer Notwendigkeit wieder im Rahmen der Allgemeinen Versicherungsbedingungen erstattet.`
      );
      scrollDown();
    } else if (
      healthHistory?.includes(HealthHistoryEnum.BREED_SPECIFIC_ILLNESS)
    ) {
      setNote(
        `Bitte beachte: Wir übernehmen keine Kosten für Behandlungsmaßnahmen oder Operationen aufgrund von angeborenen, genetischen Erbkrankheiten sowie rassespezifischen Krankheiten, für die ${name} bereits vor Versicherungsbeginn Anzeichen oder Symptome gezeigt hat.`
      );
      scrollDown();
    } else if (healthHistory?.includes(HealthHistoryEnum.CHRONIC_DISEASE)) {
      setNote(
        "Bitte beachte: Wir übernehmen keine Kosten für Behandlungsmaßnahmen oder Operationen für Verletzungen/Krankheiten, die bereits vor Versicherungsbeginn bestanden/begonnen haben und als chronisch gelten (d.h. Krankheiten, die 8 Wochen bestehen)."
      );
      scrollDown();
    } else {
      setNote("");
    }
  }, [healthHistory]);

  const options: { value: HealthHistoryEnum; label: string }[] = [
    {
      value: HealthHistoryEnum.NO_ILLNESS,
      label: "Bislang gesund und ohne Krankheiten/Verletzungen",
      //label: t("signup_health_questions.no_illness"),
    },
    {
      value: HealthHistoryEnum.TREATED_LAST_24_MONTHS,
      label: `Ist aktuell gesund, jedoch hatte ${name} in den letzten 24 Monaten Beschwerden, Krankheiten oder Verletzungen`,
      // label: t("signup_health_questions.ongoing_treatment"),
    },
    {
      value: HealthHistoryEnum.ILLNESS_LAST_24_MONTHS,
      label:
        "Hat aktuell gesundheitliche Beschwerden oder ist aktuell in tierärztlicher Behandlung",
      /*
      label: t("signup_health_questions.illness_in_24_months", {
        name: signUpState.name,
      }),

       */
    },
    {
      value: HealthHistoryEnum.BREED_SPECIFIC_ILLNESS,
      //label: t("signup_health_questions.breed_specific_illness"),
      label:
        "Hat oder hatte in der Vergangenheit Anzeichen oder Symptome von rassespezifischen Krankheiten",
    },
    {
      value: HealthHistoryEnum.CHRONIC_DISEASE,
      //label: t("signup_health_questions.chronic_decease"),
      label: "Leidet an einer oder mehreren chronischen Krankheiten",
    },
  ];

  return (
    <>
      <ChatContainer leaving={transitioning}>
        <ChatMessage>
          <FlexRow>
            <Typography>
              <Trans
                i18nKey="signup_health_questions.health"
                values={{ name }}
                components={[<strong key="0" />]}
              />
            </Typography>
          </FlexRow>
        </ChatMessage>
        <Gutter size={2} />
        {options.map((item, index) => (
          <ChatMessage
            key={item.value}
            active={healthHistory.includes(item.value)}
            blink
            fromMe
            data-testid={item.label}
            index={index + 1}
            onPress={async () => {
              dispatch(
                updateAnimalById({
                  id,
                  update: { healthHistory: [item.value] },
                })
              );
            }}
          >
            <FlexRow alignItems="center">
              <Typography>{item.label}</Typography>
            </FlexRow>
          </ChatMessage>
        ))}
        <Gutter size={0.5} />
        {note && (
          <>
            <ChatMessage index={6}>
              <Typography>{note}</Typography>
            </ChatMessage>
            <Gutter size={4} />
          </>
        )}

        <FlowButtons
          inProgress={transitioning}
          backButton={false}
          next={
            nextAnimalId ? () => dispatch(switchAnimal(nextAnimalId)) : next
          }
          disabled={!isValid}
          previous={previous}
        />
      </ChatContainer>
    </>
  );
};

export default HealthQuestions;
